import { useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { useConfig } from '@/api/queries';
import type { CapsuleView, Postcards, PostcardView, JournalEntry, Notes, NoteView, PersonTimeline, RecapView, SecondAnswer, SecondView } from './types';

/** SPEC_V9 §C endpoints (ARCHITECTURE §30). Every one is 404 feature_disabled while `after_date` is off. */
const req = api.request;
const s = (sid: string, rest: string) => `/v1/sessions/${sid}/date/${rest}`;

export const afterApi = {
  second: (sid: string) => req<SecondView>(s(sid, 'second')),
  answerSecond: (sid: string, answer: SecondAnswer) => req<SecondView>(s(sid, 'second'), { method: 'POST', body: { answer } }),
  recap: (sid: string) => req<RecapView>(s(sid, 'recap')),
  consent: (sid: string, consent: boolean) => req<RecapView>(s(sid, 'recap/consent'), { method: 'POST', body: { consent } }),
  writeNote: (sid: string, body: string) => req<NoteView>(s(sid, 'note'), { method: 'POST', body: { body } }),
  /** v13 time capsule: opens at the pair's next date. */
  capsule: (sid: string) => req<CapsuleView>(s(sid, 'capsule')),
  sealCapsule: (sid: string, body: string) => req<CapsuleView>(s(sid, 'capsule'), { method: 'POST', body: { body } }),
  notes: () => req<Notes>('/v1/me/notes'),
  openNote: (id: string) => req<NoteView>(`/v1/notes/${id}/open`, { method: 'POST' }),
  reportNote: (id: string, reason: string, block: boolean) => req<void>(`/v1/notes/${id}/report`, { method: 'POST', body: { reason, block } }),
  journal: () => req<{ dates: JournalEntry[] }>('/v1/me/dates'),
  entry: (sid: string) => req<JournalEntry>(`/v1/me/dates/${sid}`),
  saveJournal: (sid: string, patch: { note?: string; would_see_again?: boolean | null }) => req<JournalEntry>(`/v1/me/dates/${sid}/journal`, { method: 'PUT', body: patch }),
  person: (userId: string) => req<PersonTimeline>(`/v1/me/people/${userId}/dates`),
  /** v13 postcards between dates. */
  postcards: (userId: string) => req<Postcards>(`/v1/me/people/${userId}/postcards`),
  sendPostcard: (userId: string, body: string) => req<PostcardView>(`/v1/me/people/${userId}/postcards`, { method: 'POST', body: { body } }),
  reportPostcard: (id: string, reason: string, block: boolean) => req<void>(`/v1/postcards/${id}/report`, { method: 'POST', body: { reason, block } }),
};

export const afterKeys = {
  second: (sid: string) => ['after', 'second', sid] as const,
  recap: (sid: string) => ['after', 'recap', sid] as const,
  notes: ['after', 'notes'] as const,
  capsule: (sid: string) => ['after', 'capsule', sid] as const,
  journal: ['after', 'journal'] as const,
  entry: (sid: string) => ['after', 'entry', sid] as const,
  person: (userId: string) => ['after', 'person', userId] as const,
  postcards: (userId: string) => ['after', 'postcards', userId] as const,
};

/** The runtime flag (default on; needs date_night). Missing from an older server ⇒ off. */
export function useAfterOn(): boolean {
  const c = useConfig();
  return c.data?.features.after_date === true && c.data.features.date_night !== false;
}

export const useSecond = (sid: string, on = true) => useQuery({ queryKey: afterKeys.second(sid), queryFn: () => afterApi.second(sid), enabled: on, retry: false });
/** `fresh`: refetch whenever a card mounts, so consent shown is never stale. */
export const useRecap = (sid: string, on = true, fresh = false) =>
  useQuery({ queryKey: afterKeys.recap(sid), queryFn: () => afterApi.recap(sid), enabled: on, retry: false, refetchOnMount: fresh ? 'always' : true });
export const useCapsule = (sid: string, on = true) => useQuery({ queryKey: afterKeys.capsule(sid), queryFn: () => afterApi.capsule(sid), enabled: on, retry: false });
export const useNotes = (on = true) => useQuery({ queryKey: afterKeys.notes, queryFn: afterApi.notes, enabled: on });
export const useJournal = (on = true) => useQuery({ queryKey: afterKeys.journal, queryFn: afterApi.journal, enabled: on });
export const useEntry = (sid: string, on = true) => useQuery({ queryKey: afterKeys.entry(sid), queryFn: () => afterApi.entry(sid), enabled: on, retry: false });
export const usePostcards = (userId: string, on = true) => useQuery({ queryKey: afterKeys.postcards(userId), queryFn: () => afterApi.postcards(userId), enabled: on, retry: false });
export const usePerson = (userId: string, on = true) => useQuery({ queryKey: afterKeys.person(userId), queryFn: () => afterApi.person(userId), enabled: on, retry: false });
